import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import { transformSkipReasonWord } from "@shared/runs/transformSkipReasons";
import type { DbtJobRunRow } from "@shared/types/transformations";

/**
 * One vocabulary for a transform run's outcome (ux-s11-transformations.md §3.3, ledger H6): the
 * History view's Status column, the landing's status line and its action bars (P5 imports this)
 * all read a run through `runOutcome`, so "what does a clone failure say" has one answer.
 *
 * Words: Succeeded, Tests failed, Failed, Could not clone, Skipped, Cancelled, Running, Queued.
 * Colour always rides with the word (never colour alone).
 *
 * A `stopped` outcome is a failure BEFORE dbt read the project (the container's setup phases:
 * config, auth, guard, clone, deps). #1207 round 3: EVERY failed run's word is `failedRunWord`
 * of its exit class, the one rule the dbt job header (`stepStatusPresentation`) also reads, so a
 * run reads one word on every screen: "Tests failed", "Model failed" (exit class `run`), "Could
 * not clone", else "Failed", never dbt's phase token ("Stopped at deps"). The phase only picks the
 * kind and rides in `phase`, and the line beside the word says it in words (`phasePhrase`:
 * "Stopped while installing packages.").
 */

export type RunOutcomeKind =
  | "succeeded"
  | "tests_failed"
  | "failed"
  | "stopped"
  | "skipped"
  | "cancelled"
  | "running"
  | "queued";

export interface RunOutcome {
  kind: RunOutcomeKind;
  /** The word on screen: "Succeeded", "Could not clone", … */
  word: string;
  color: SkyBadgeColor;
  /** True while the run can still change (queued or running). */
  inFlight: boolean;
  /** For `stopped`: the phase the run stopped at; null otherwise. */
  phase: string | null;
}

/** The container's setup phases: a failure in one of them is a stop before dbt read the project. */
export const SETUP_PHASES: ReadonlySet<string> = new Set([
  "config",
  "auth",
  "guard",
  "clone",
  "deps",
]);

/**
 * A phase in words (UX §2.2): "stopped while cloning the repository". #1207 round 2: the ONE map
 * of dbt's phase tokens to words. A stopped run's line (`phasePhrase`) and an active run's line
 * (`phaseWords` in `transformFormatting.ts`, which capitalises it) both read it, so one phase
 * reads one way whether the run is active or stopped.
 */
const PHASE_PHRASES: Readonly<Record<string, string>> = {
  config: "reading the configuration",
  auth: "signing in to the warehouse",
  guard: "checking the warehouse",
  clone: "cloning the repository",
  deps: "installing packages",
  compile: "compiling the project",
  run: "running models",
  test: "running tests",
};

/** Whether dbt's phase token has words in the one map. */
export function isKnownPhase(phase: string): boolean {
  return Object.hasOwn(PHASE_PHRASES, phase);
}

export function phasePhrase(phase: string): string {
  return PHASE_PHRASES[phase] ?? phase;
}

/** The phase a failed setup stopped at: the step's `phase`, else its exit class. */
function stoppedPhase(row: Pick<DbtJobRunRow, "phase" | "exitClass">): string | null {
  const phase = row.phase ?? row.exitClass;
  return phase && SETUP_PHASES.has(phase) ? phase : null;
}

/**
 * #1207 round 3: a failed run's word from its exit class alone. `stepStatusPresentation` (the dbt
 * job header, its Runs tab, a model's History) and `runOutcome` (Transformations History, the
 * drawer, the Models view, the landing) both read it.
 */
export function failedRunWord(exitClass: string | null): string {
  switch (exitClass) {
    case "test":
      return "Tests failed";
    case "run":
      return "Model failed";
    case "clone":
      return "Could not clone";
    default:
      return "Failed";
  }
}

/** Round 6, T1: the words of a step waiting for the instance's one dbt slot. */
export function queuedBehindWord(runId: string): string {
  return `Queued: waiting for run ${runId}`;
}

export function runOutcome(
  row: Pick<DbtJobRunRow, "stepStatus" | "exitClass" | "phase"> &
    Partial<Pick<DbtJobRunRow, "queuedBehindRunId">>,
): RunOutcome {
  const base = { inFlight: false, phase: null };
  switch (row.stepStatus) {
    case "pending":
      return {
        kind: "queued",
        // T1: a step waiting behind another dbt run says which one, never a bare "Queued".
        word: row.queuedBehindRunId ? queuedBehindWord(row.queuedBehindRunId) : "Queued",
        color: "informative",
        inFlight: true,
        phase: null,
      };
    case "running":
      return {
        kind: "running",
        word: "Running",
        color: "informative",
        inFlight: true,
        phase: null,
      };
    case "succeeded":
      return { ...base, kind: "succeeded", word: "Succeeded", color: "success" };
    case "skipped":
      return { ...base, kind: "skipped", word: "Skipped", color: "warning" };
    case "cancelled":
      return { ...base, kind: "cancelled", word: "Cancelled", color: "subtle" };
    case "failed":
      break;
  }
  const word = failedRunWord(row.exitClass);
  if (row.exitClass === "test") {
    return { ...base, kind: "tests_failed", word, color: "danger" };
  }
  const phase = stoppedPhase(row);
  if (phase) {
    return {
      kind: "stopped",
      word,
      color: "danger",
      inFlight: false,
      phase,
    };
  }
  return { ...base, kind: "failed", word, color: "danger" };
}

/** Whether the row is still moving (the History re-reads only then). */
export function isRunInFlight(row: Pick<DbtJobRunRow, "stepStatus">): boolean {
  return row.stepStatus === "pending" || row.stepStatus === "running";
}

/**
 * The runner's skip token (`TRANSFORM_SKIP_REASONS`, `runner/steps/transform.ts`) in words, for
 * "the last run was skipped ({reason word})". An unknown token is shown as it is (it is a closed
 * token, never free text).
 */
export function skipReasonWord(token: string | null): string {
  if (!token) return "no reason recorded";
  return transformSkipReasonWord(token) ?? token;
}
